import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePortalBase } from "@/hooks/usePortalBase";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormGuard } from "@/hooks/useFormGuard";
import { DiscardChangesDialog, UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";
import { useCreateContractWithInvoices } from "@/hooks/useContracts";
import { useContractUnits } from "@/hooks/useContractUnits";
import { sumUnitRents, generateAutoSplitSchedule, deriveCanonicalTotal, computeDownPayment, saleScheduledBalance } from "@/lib/contractHelpers";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, X, Check, ArrowLeft, ArrowRight, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { BrokerageContractUploadWizard } from "@/components/brokerage/BrokerageContractUploadWizard";
import { CONTRACT_TYPES } from "./types";

import { ContractInfoStep } from "./ContractInfoStep";
import { PaymentInfoStep } from "./PaymentInfoStep";
import { ServicesStep } from "./ServicesStep";
import { ReviewStep } from "./ReviewStep";
import {
  ContractWizardFormData,
  ContractUnitFormRow,
  WizardStep,
  STEP_META,
  activeStepKeys,
  initialFormData,
  emptyContractUnitRow,
  PaymentScheduleItem,
  AnyServiceKey,
  ALL_SERVICE_KEYS,
  emptyServiceEntry,
  SERVICE_UTILITY_KEYS,
  UtilityType,
  Payer,
} from "./types";

interface ContractWizardDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editMode?: boolean;
  contractData?: Partial<ContractWizardFormData>;
  contractId?: string;
  contractStatus?: string;
  // Create-mode pre-selection (e.g. opened from a specific unit/property page,
  // or the "Add Contract" menu deep-linking straight into the brokerage flow).
  // The Master Card "activate draft" flow passes the full set (dates, amount,
  // deposit, frequency, notes) so the owner-approved draft is carried in verbatim.
  prefill?: {
    propertyId?: string;
    unitId?: string;
    contractType?: ContractWizardFormData["contractType"];
    startDate?: string;
    endDate?: string;
    totalAmount?: string;
    depositAmount?: string;
    paymentFrequency?: ContractWizardFormData["paymentFrequency"];
    notes?: string;
  };
  // When set, a successful create calls this with the new contract id INSTEAD of
  // navigating to the contract page — the caller (e.g. the Master Card activate
  // flow) takes over from there.
  onCreated?: (contractId: string) => void;
}

export function ContractWizardDrawer({
  open,
  onOpenChange,
  editMode = false,
  contractData,
  contractId,
  contractStatus,
  prefill,
  onCreated,
}: ContractWizardDrawerProps) {
  const { t, direction } = useLanguage();
  const navigate = useNavigate();
  const portalBase = usePortalBase();
  const createContract = useCreateContractWithInvoices();

  const [currentStep, setCurrentStep] = useState<WizardStep>("info");
  const [formData, setFormData] = useState<ContractWizardFormData>(initialFormData);
  // Active step keys for the current contract/sale type + the current position
  // in that list. Sale contracts drop Services (and immediate sales also drop
  // Payment), so the wizard's length/numbering is data-driven, not fixed at 4.
  const steps = useMemo(
    () => activeStepKeys(formData.contractType, formData.saleType),
    [formData.contractType, formData.saleType],
  );
  // NOT covered: formData.units[] row mutations (rentAmount, service fees).
  // Safe today because editing a unit row requires selecting a property first,
  // which sets propertyId (tracked below) — revisit if a step-1 refactor ever
  // exposes unit inputs before the property picker.
  // #3069 added the Ejar-style Y/M/D duration entry (durationYears/Months/
  // Days, all "" by default) — a user can type a duration BEFORE picking a
  // startDate (handleDurationChange in ContractInfoStep updates them
  // unconditionally), so the dirty check must cover them independently of
  // startDate or the #3018 Escape guard silently drops that input.
  const isDirty = useMemo(() => !!(
    formData.propertyId || formData.unitId || formData.tenantId ||
    formData.startDate || formData.endDate || formData.totalAmount || formData.depositAmount ||
    formData.notes ||
    formData.durationYears || formData.durationMonths || formData.durationDays
  ), [formData.propertyId, formData.unitId, formData.tenantId, formData.startDate, formData.endDate, formData.totalAmount, formData.depositAmount, formData.notes, formData.durationYears, formData.durationMonths, formData.durationDays]);
  const blocker = useFormGuard(open && isDirty);
  const [originalData, setOriginalData] = useState<ContractWizardFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // #3018 — Escape (e.g. pressed a beat after the DatePicker closes), an
  // overlay click, or the X all reach the Radix Dialog's onOpenChange(false)
  // directly, bypassing the router-level useFormGuard and silently dropping up
  // to 3 steps of input. Route every close request through a dirty check; the
  // post-save close in handleSubmit calls onOpenChange(false) directly on
  // purpose (nothing unsaved left to lose).
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const handleDrawerOpenChange = (next: boolean) => {
    if (!next && isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(next);
  };

  // Pull junction rows when editing an existing contract so the per-unit
  // picker can be re-populated. For new contracts (no contractId), or
  // for legacy single-unit contracts (junction empty), the hook returns
  // [] and we fall back to the initial 1-row default.
  const { data: editJunctionRows } = useContractUnits(
    editMode ? contractId ?? null : null
  );

  // Reset form when dialog opens/closes.
  //
  // For draft/edit reload of a multi-unit contract: contractData carries
  // only the legacy contract-level fields (rent_amount, unit_id, etc.).
  // Without explicit logic the new `units` array on formData stays at the
  // initialFormData default (1 empty row), which is what made A8/B3 fail
  // QA — the wizard looked empty when re-opened. We now hydrate
  // formData.units from contract_units junction rows fetched above.
  useEffect(() => {
    if (open) {
      if (editMode && contractData) {
        const rebuiltUnits: ContractUnitFormRow[] | undefined =
          editJunctionRows && editJunctionRows.length > 0
            ? editJunctionRows.map((row) => {
                const serviceFees = ALL_SERVICE_KEYS.reduce(
                  (acc, key) => {
                    const isUtility = (
                      SERVICE_UTILITY_KEYS as readonly string[]
                    ).includes(key);
                    const raw = (row.service_fees as Record<string, unknown> | null)?.[key];
                    // raw can be: { amount, payer, type? } | plain-number | undefined
                    if (raw && typeof raw === "object") {
                      const obj = raw as { amount?: number | null; payer?: Payer; type?: UtilityType };
                      acc[key] = {
                        amount: obj.amount != null ? String(obj.amount) : "",
                        payer: obj.payer ?? "tenant",
                        ...(isUtility ? { type: obj.type ?? "not_available" } : {}),
                      };
                    } else if (typeof raw === "number") {
                      // Legacy plain-number row (pre-PR-1394). Default
                      // payer=tenant for backwards compat.
                      acc[key] = {
                        amount: String(raw),
                        payer: "tenant",
                        ...(isUtility ? { type: "not_available" } : {}),
                      };
                    } else {
                      acc[key] = emptyServiceEntry({ utility: isUtility });
                    }
                    return acc;
                  },
                  {} as Record<AnyServiceKey, ReturnType<typeof emptyServiceEntry>>
                );
                return {
                  unitId: row.unit_id,
                  rentAmount: row.rent_amount != null ? String(row.rent_amount) : "",
                  serviceFees,
                };
              })
            : undefined;

        const data: ContractWizardFormData = {
          ...initialFormData,
          ...contractData,
          // Junction rows take precedence over the legacy single unitId
          // mirror when available; otherwise keep the default single-row
          // shape with the contract's unit_id pre-filled if present.
          units: rebuiltUnits ?? initialFormData.units,
        };
        setFormData(data);
        setOriginalData(data);
      } else {
        // Create mode: optionally pre-select a property/unit when the wizard
        // was opened from a specific unit/property page.
        const hasPrefill =
          prefill?.propertyId || prefill?.unitId || prefill?.contractType ||
          prefill?.startDate || prefill?.endDate || prefill?.totalAmount ||
          prefill?.depositAmount || prefill?.paymentFrequency || prefill?.notes;
        const seeded: ContractWizardFormData = hasPrefill
          ? {
              ...initialFormData,
              contractType: prefill!.contractType ?? initialFormData.contractType,
              propertyId: prefill!.propertyId ?? initialFormData.propertyId,
              unitId: prefill!.unitId ?? initialFormData.unitId,
              startDate: prefill!.startDate ?? initialFormData.startDate,
              endDate: prefill!.endDate ?? initialFormData.endDate,
              totalAmount: prefill!.totalAmount ?? initialFormData.totalAmount,
              depositAmount: prefill!.depositAmount ?? initialFormData.depositAmount,
              paymentFrequency: prefill!.paymentFrequency ?? initialFormData.paymentFrequency,
              notes: prefill!.notes ?? initialFormData.notes,
              units: prefill!.unitId
                ? [
                    {
                      ...emptyContractUnitRow,
                      serviceFees: { ...emptyContractUnitRow.serviceFees },
                      unitId: prefill!.unitId,
                    },
                  ]
                : initialFormData.units,
            }
          : initialFormData;
        setFormData(seeded);
        setOriginalData(seeded);
      }
      setCurrentStep("info");
      setErrors({});
    }
  }, [open, editMode, contractData, editJunctionRows, prefill]);

  // Keep the current step valid when the active step set changes mid-flow — e.g.
  // switching contract type to an immediate sale while sitting on Payment/Services
  // (those keys vanish). Snap back to the nearest still-present earlier step.
  useEffect(() => {
    if (!steps.includes(currentStep)) {
      setCurrentStep(steps[0]);
    }
  }, [steps, currentStep]);

  // Keyed on `open` alone — the reset effect above re-runs whenever a parent
  // passes fresh contractData/prefill identities, and a confirm dialog that's
  // legitimately showing must survive those re-runs.
  useEffect(() => {
    if (!open) setConfirmDiscardOpen(false);
  }, [open]);

  const updateFormData = (
    updates: Partial<ContractWizardFormData> | ((prev: ContractWizardFormData) => ContractWizardFormData),
  ) => {
    if (typeof updates === "function") {
      setFormData(updates);
    } else {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  };

  const isSale = formData.contractType === "sale";
  // End date ("delivery date" for off-plan) is only required for a schedule-bearing
  // term: rent/sublease, or an OFF-PLAN sale. An immediate sale has no term.
  const endDateRequired = !isSale || formData.saleType === "offplan";

  const validateStep = (step: WizardStep): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case "info":
        if (!formData.propertyId) {
          newErrors.property = t("يجب اختيار العقار", "Property is required");
        }
        if (!formData.unitId) {
          newErrors.unit = t("يجب اختيار الوحدة", "Unit is required");
        }
        if (!formData.tenantId) {
          newErrors.tenant = t("يجب اختيار الطرف", "Party is required");
        }
        if (!formData.startDate) {
          newErrors.startDate = t("يجب تحديد تاريخ البداية", "Start date is required");
        }
        // End date is no longer auto-forced from a default duration (it's
        // now directly editable / derived from the optional Ejar-style Y/M/D
        // fields — see ContractInfoStep) so it needs its own explicit gate —
        // but ONLY when the contract actually has a term (see endDateRequired).
        if (endDateRequired && !formData.endDate) {
          newErrors.endDate = isSale
            ? t("يجب تحديد تاريخ التسليم", "Delivery date is required")
            : t("يجب تحديد تاريخ النهاية", "End date is required");
        }
        if (!formData.totalAmount || Number(formData.totalAmount) <= 0) {
          newErrors.totalAmount = t("المبلغ يجب أن يكون أكبر من صفر", "Amount must be greater than zero");
        }
        break;

      case "payment":
        if (!formData.paymentsCount || Number(formData.paymentsCount) <= 0) {
          newErrors.paymentsCount = t("يجب تحديد عدد الدفعات", "Number of payments is required");
        }
        if (!formData.firstPaymentDate) {
          newErrors.firstPaymentDate = t("يجب تحديد تاريخ أول دفعة", "First payment date is required");
        }
        break;

      case "services":
      case "review":
        // No required fields.
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const currentIndex = steps.indexOf(currentStep);

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(steps[Math.min(currentIndex + 1, steps.length - 1)]);
    }
  };

  const handleBack = () => {
    setCurrentStep(steps[Math.max(currentIndex - 1, 0)]);
  };

  const goToStep = (step: WizardStep) => {
    if (steps.includes(step)) setCurrentStep(step);
  };

  // Contract duration in months — single source of truth shared by the schedule
  // generator and the submit handler. It was computed twice in separate scopes,
  // which let the canonical total diverge between the preview and what gets
  // persisted; one memo keeps them identical.
  const contractMonths = useMemo(() => {
    const durationValue = Number(formData.durationValue) || 12;
    switch (formData.durationUnit) {
      case "day":
        return Math.max(1, Math.floor(durationValue / 30));
      case "year":
        return durationValue * 12;
      case "month":
      default:
        return durationValue;
    }
  }, [formData.durationValue, formData.durationUnit]);

  // SALE contracts: the scheduled/invoiced amount is the sale price MINUS the
  // down payment (spec item 2 — down payment deducted from the total). The
  // header total_amount stays the full sale price (added back server-side); only
  // the balance is scheduled. Immediate → one payment at the sale date; off-plan
  // → the balance split lease-style across the chosen frequency.
  const saleDownPayment = useMemo(
    () =>
      isSale
        ? computeDownPayment(
            Number(formData.totalAmount) || 0,
            Number(formData.depositAmount) || 0,
            formData.downPaymentType,
          )
        : 0,
    [isSale, formData.totalAmount, formData.depositAmount, formData.downPaymentType],
  );

  const generateSaleSchedule = (): PaymentScheduleItem[] => {
    const balance = saleScheduledBalance(Number(formData.totalAmount) || 0, saleDownPayment);
    if (balance <= 0) return [];
    if (formData.saleType === "immediate") {
      const dueDate = formData.firstPaymentDate || formData.startDate;
      if (!dueDate) return [];
      return [{ paymentNumber: 1, dueDate, amount: balance }];
    }
    // off-plan: lease-style split of the balance
    if (formData.paymentFrequency === "custom") {
      return (formData.customPaymentSchedule || []).map((p, i) => ({ ...p, paymentNumber: i + 1 }));
    }
    const paymentsPerYear = Number(formData.paymentsCount) || 0;
    const firstDate = formData.firstPaymentDate;
    if (paymentsPerYear <= 0 || !firstDate) return [];
    return generateAutoSplitSchedule(balance, paymentsPerYear, contractMonths, firstDate);
  };

  // Generate payment schedule
  const generatePaymentSchedule = (): PaymentScheduleItem[] => {
    if (isSale) return generateSaleSchedule();
    // Custom mode: use the user-defined schedule directly.
    if (formData.paymentFrequency === "custom") {
      return (formData.customPaymentSchedule || []).map((p, i) => ({
        ...p,
        paymentNumber: i + 1,
      }));
    }

    const paymentsPerYear = Number(formData.paymentsCount) || 0;
    const enteredAmount = Number(formData.totalAmount) || 0;
    const firstDate = formData.firstPaymentDate;

    if (paymentsPerYear <= 0 || enteredAmount <= 0 || !firstDate) return [];

    // ONE canonical full-term total (shared with handleSubmit via the same helper
    // + contractMonths) so the schedule sum === the persisted total_amount === the
    // FlexiblePaymentEditor target — no spurious زيادة/نقص and no drift.
    const canonicalTotal = deriveCanonicalTotal(enteredAmount, formData.rentBasis, {
      startDate: formData.startDate,
      endDate: formData.endDate,
      contractMonths,
    });

    if (canonicalTotal <= 0) return [];

    // Distribute canonical total so sum(schedule) === target exactly.
    return generateAutoSplitSchedule(canonicalTotal, paymentsPerYear, contractMonths, firstDate);
  };

  // Helper to compute fee amount from FeeConfig
  const computeFeeAmount = (fee: { type: string; value: string }, totalAmount: string): number => {
    if (!fee.value) return 0;
    if (fee.type === "percentage") {
      return (Number(totalAmount) * Number(fee.value)) / 100;
    }
    return Number(fee.value);
  };

  const handleSubmit = async (asDraft: boolean = true) => {
    // Fail-closed: brokerage contracts are saved by BrokerageContractUploadWizard,
    // never by the regular createContract RPC. The footer (and these buttons) is
    // hidden in brokerage mode, so this is unreachable today — the guard keeps the
    // invariant structural rather than UI-only, surviving future refactors.
    if (formData.contractType === "brokerage") return;
    try {
      const paymentSchedule = generatePaymentSchedule();

      // Canonical full-term total — the amount the schedule actually sums to.
      // Derived with the SAME helper + contractMonths as generatePaymentSchedule,
      // so total_amount, rent_amount and the percentage-fee bases stay in lockstep
      // with the invoices. The field is the annual rate, so a multi-year contract
      // multiplies the full years and a partial final period is prorated on REGA's
      // 364-day year (otherwise a 2yr contract would persist/bill a single year).
      // SALE: total_amount is the full negotiated sale price (a term_total, NOT
      // an annual rate) — deriveCanonicalTotal would wrongly multiply it by the
      // term. The down payment is deducted from the SCHEDULE (generateSaleSchedule),
      // never from the header total. RENT/SUBLEASE: unchanged annual-rate derivation.
      const submittedTotal = isSale
        ? Number(formData.totalAmount) || 0
        : deriveCanonicalTotal(Number(formData.totalAmount) || 0, formData.rentBasis, {
            startDate: formData.startDate,
            endDate: formData.endDate,
            contractMonths,
          });

      // Fee bases — CEO 2026-07-05/06: ALL four fees are % of the FIRST-YEAR
      // rent, never the full multi-year total (a 3yr/120k-yr/2.5% contract
      // must charge 2.5% × 120k = 3,000, not 2.5% × 360k = 9,000). This
      // wizard's amount field is ALWAYS the annual rate (rentBasis is
      // hardcoded 'annual' — SHOW_RENT_BASIS_TOGGLE is false in
      // PaymentInfoStep), so the entered value pre-derive already IS the
      // first-year rent; no inversion needed. property_management_fee
      // (إدارة الأملاك) stores a PER-YEAR amount — the create RPC fans it out
      // into one invoice per contract year (CEO 2026-07-06 swap); the other
      // three fees stay one-time invoices.
      const firstYearRent = Number(formData.totalAmount) || 0;
      const brokerageFeeAmount = computeFeeAmount(formData.brokerageFee, String(firstYearRent));
      const marketingFeeAmount = computeFeeAmount(formData.marketingFee, String(firstYearRent));
      const contractMgmtFeeAmount = computeFeeAmount(formData.contractManagementFee, String(firstYearRent));
      // No property-management fee on SALE contracts (spec item 3) — the FeeRow
      // is hidden for sales; zero it here too so a stale value can never invoice.
      const propertyMgmtFeeAmount = isSale
        ? 0
        : computeFeeAmount(formData.propertyManagementFee, String(firstYearRent));

      // Build the multi-unit payload from the per-unit form rows. We only
      // send it when the user has actually filled at least one row with a
      // unit_id — that way an entirely-blank `units` array (e.g. legacy
      // form data) still takes the single-unit path in the RPC.
      const filledUnits = formData.units.filter((row) => row.unitId);
      // Build the new per-unit service_fees shape: each key maps to
      // `{ amount, payer, type? }`. Empty / 0-amount entries are dropped
      // before send so the RPC's JSONB stays compact. The RPC reads this
      // shape AND legacy plain-number shapes defensively.
      const unitsPayload = filledUnits.map((row) => ({
        unit_id: row.unitId,
        // Round to 2dp so the JSONB value is an exact decimal that matches
        // what Postgres casts to numeric — prevents the per-unit sum from
        // diverging from sumUnitRents() and triggering the RPC guard (#2483).
        rent_amount: Math.round((row.rentAmount ? Number(row.rentAmount) : 0) * 100) / 100,
        service_fees: Object.fromEntries(
          Object.entries(row.serviceFees)
            .filter(([, entry]) => entry.amount && Number(entry.amount) > 0)
            .map(([key, entry]) => [
              key,
              {
                amount: Number(entry.amount),
                payer: entry.payer,
                ...(entry.type ? { type: entry.type } : {}),
              },
            ])
        ),
      }));
      // Multi-unit branch detection. When sending a multi-unit payload,
      // the contract's rent_amount must equal the sum of per-unit rents
      // (RPC enforces this). For legacy single-unit, keep the old behavior
      // where rent_amount mirrors totalAmount.
      const usingMultiUnit =
        filledUnits.length > 1 ||
        (filledUnits.length === 1 &&
          Object.values(filledUnits[0].serviceFees).some(
            (entry) => entry.amount && Number(entry.amount) > 0
          ));
      const unitsRentSum = sumUnitRents(
        unitsPayload.map((u) => ({ rent_amount: u.rent_amount }))
      );

      // contract_fee is kept as the aggregate legacy column for tenant-facing
      // RPC fallback paths. The legacy `management_fee` column write was
      // dropped (was a duplicate of property_management_fee and produced a
      // phantom value with no matching invoice — see CU-86expdt4n
      // dashboard-audit). Modern code reads `property_management_fee` as the
      // source of truth for ongoing management charges.
      const contract = await createContract.mutateAsync({
        property_id: formData.propertyId,
        unit_id: formData.unitId,
        tenant_id: formData.tenantId,
        contract_type: formData.contractType as any,
        start_date: formData.startDate,
        end_date: formData.endDate,
        rent_amount:
          formData.contractType === "rent" || formData.contractType === "sublease"
            ? usingMultiUnit
              ? unitsRentSum
              : submittedTotal
            : undefined,
        total_amount: submittedTotal,
        // SALE: persist the resolved DOWN PAYMENT (fixed SAR or % of sale price)
        // — deducted from the scheduled balance, added back into the header total
        // server-side. RENT/SUBLEASE: the security deposit, as entered.
        deposit_amount: isSale
          ? saleDownPayment > 0
            ? saleDownPayment
            : undefined
          : formData.depositAmount
          ? Number(formData.depositAmount)
          : undefined,
        payments_count: paymentSchedule.length,
        notes: formData.notes || undefined,
        // Only send when there's more than 1 unit OR the user expressed
        // per-unit service fees on the single row. Otherwise the RPC's
        // legacy single-unit path is sufficient and the rent_amount sum
        // assertion inside the RPC stays trivially satisfied.
        units: usingMultiUnit ? unitsPayload : undefined,
        contract_fee: brokerageFeeAmount + marketingFeeAmount + contractMgmtFeeAmount,
        contract_fee_payer: formData.contractManagementFee.payer || 'tenant',
        // New fee structure for fee invoices
        brokerage_fee: brokerageFeeAmount,
        brokerage_fee_payer: formData.brokerageFee.payer,
        brokerage_fee_type: formData.brokerageFee.type,
        brokerage_fee_value: formData.brokerageFee.value,
        marketing_fee: marketingFeeAmount,
        marketing_fee_payer: formData.marketingFee.payer,
        marketing_fee_type: formData.marketingFee.type,
        marketing_fee_value: formData.marketingFee.value,
        contract_management_fee: contractMgmtFeeAmount,
        contract_management_fee_payer: formData.contractManagementFee.payer,
        contract_management_fee_type: formData.contractManagementFee.type,
        contract_management_fee_value: formData.contractManagementFee.value,
        property_management_fee: propertyMgmtFeeAmount,
        property_management_fee_payer: formData.propertyManagementFee.payer,
        property_management_fee_type: formData.propertyManagementFee.type,
        // Blank the % value on sales so no stale property-mgmt rate is persisted.
        property_management_fee_value: isSale ? "" : formData.propertyManagementFee.value,
        paymentSchedule,
      });

      toast.success(t("تم إنشاء العقد بنجاح", "Contract created successfully"));
      onOpenChange(false);
      const contractResult = contract as { id?: string } | null;
      // Master Card activation takes over from here (link + activate the unit)
      // instead of leaving for the contract page.
      if (onCreated && contractResult?.id) {
        onCreated(contractResult.id);
        return;
      }
      navigate(`${portalBase}/contracts/${contractResult?.id ?? ""}`);
    } catch (error) {
      console.error("Error creating contract:", error);
      toast.error(t("حدث خطأ أثناء إنشاء العقد", "Error creating contract"));
    }
  };

  // Brokerage is a UI-only branch: selecting "brokerage" in the type selector
  // swaps the whole 4-step body for the existing brokerage upload wizard, which
  // owns its own steps/footer and saves to the Nest brokerage slice. Never in
  // edit mode (that only ever loads regular rent/sale/sublease contracts).
  const isBrokerage = !editMode && formData.contractType === "brokerage";

  // The contract-type selector, also rendered at the top of the brokerage body
  // so the user can switch back to a regular contract.
  const renderTypeSelector = () => (
    <div className="space-y-2">
      <Label>
        {t("نوع العقد", "Contract Type")} <span className="text-destructive">*</span>
      </Label>
      <Select
        value={formData.contractType}
        onValueChange={(val) => updateFormData({ contractType: val as ContractWizardFormData["contractType"] })}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONTRACT_TYPES
            // Mirror ContractInfoStep: brokerage is a dashboard-only flow.
            .filter((type) => type.value !== "brokerage" || portalBase === "/dashboard")
            .map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {t(type.label, type.labelEn)}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderBrokerageBody = () => (
    <div className="space-y-6">
      {renderTypeSelector()}
      <BrokerageContractUploadWizard
        initialMode="upload"
        onSuccess={(id) => {
          onOpenChange(false);
          navigate(`/dashboard/brokerage/${id}`);
        }}
        onCancel={() => handleDrawerOpenChange(false)}
      />
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case "info":
        return (
          <ContractInfoStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case "payment":
        return (
          <PaymentInfoStep
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case "services":
        return <ServicesStep formData={formData} updateFormData={updateFormData} />;
      case "review":
        return (
          <ReviewStep
            formData={formData}
            onGoToStep={goToStep}
            isEditMode={editMode}
            originalData={originalData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDrawerOpenChange}>
        <DialogContent className="max-w-2xl h-[85vh] p-0 flex flex-col gap-0 overflow-hidden [&>button:last-of-type]:hidden" data-testid="contract-wizard">
          <div className="flex flex-col h-full min-h-0">
            {/* Header */}
            <DialogHeader className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-xl font-bold">
                  {editMode
                    ? t("تعديل العقد", "Edit Contract")
                    : t("إضافة عقد جديد", "Add New Contract")}
                </DialogTitle>
                <Button variant="ghost" size="icon" onClick={() => handleDrawerOpenChange(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <DialogDescription className="sr-only">
                {editMode
                  ? t("عدّل بيانات العقد الحالي بما في ذلك الأطراف والدفعات والخدمات", "Edit the current contract details including parties, payment schedule, and services")
                  : t("أنشئ عقداً جديداً بتحديد العقار والمستأجر وجدول الدفعات والخدمات", "Create a new contract by specifying the property, tenant, payment schedule, and services")}
              </DialogDescription>

              {/* Step Indicator — hidden in brokerage mode (the upload wizard owns its own steps) */}
              {!isBrokerage && (
              <div className="flex items-center justify-center mt-4 gap-1">
                {steps.map((stepKey, index) => (
                  <div key={stepKey} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (index < currentIndex || validateStep(currentStep)) {
                          goToStep(stepKey);
                        }
                      }}
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors",
                        currentStep === stepKey
                          ? "bg-primary text-primary-foreground"
                          : index < currentIndex
                          ? "bg-primary/20 text-primary"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {index < currentIndex ? <Check className="h-4 w-4" /> : index + 1}
                    </button>
                    {index < steps.length - 1 && (
                      <div
                        className={cn(
                          "w-8 h-0.5 mx-1",
                          index < currentIndex ? "bg-primary" : "bg-muted"
                        )}
                      />
                    )}
                  </div>
                ))}
              </div>
              )}

              {/* Step Title — hidden in brokerage mode */}
              {!isBrokerage && (
              <div className="text-center mt-2">
                <p className="text-sm font-medium">
                  {t(STEP_META[currentStep].label, STEP_META[currentStep].labelEn)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t(
                    `الخطوة ${currentIndex + 1} من ${steps.length}`,
                    `Step ${currentIndex + 1} of ${steps.length}`,
                  )}
                </p>
              </div>
              )}
            </DialogHeader>

            {/* Edit Mode Warning */}
            {editMode && contractStatus === "active" && (
              <Alert variant="destructive" className="mx-6 mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t(
                    "تحذير: هذا العقد نشط. أي تعديل سيؤثر على العقد الحالي.",
                    "Warning: This contract is active. Any changes will affect the current contract."
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              {isBrokerage ? renderBrokerageBody() : renderStepContent()}
            </div>

            {/* Footer — the brokerage upload wizard renders its own footer */}
            {!isBrokerage && (
            <DialogFooter className="border-t px-6 py-4 sm:justify-between shrink-0">
              <div className="flex items-center justify-between w-full">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentIndex === 0}
                  className="gap-2"
                >
                  {direction === "rtl" ? (
                    <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                  ) : (
                    <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
                  )}
                  {t("السابق", "Previous")}
                </Button>

                <div className="flex gap-2">
                  {currentStep === "review" ? (
                    <>
                      <Button
                        data-testid="contract-save-draft-btn"
                        variant="outline"
                        onClick={() => handleSubmit(true)}
                        disabled={createContract.isPending}
                        className="gap-2"
                      >
                        <Save className="h-4 w-4" />
                        {t("حفظ كمسودة", "Save as Draft")}
                      </Button>
                      <Button
                        onClick={() => handleSubmit(false)}
                        disabled={createContract.isPending}
                        className="gap-2 min-w-[120px]"
                        data-testid="contract-submit"
                      >
                        {createContract.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t("جاري الحفظ...", "Saving...")}
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            {t("حفظ", "Save")}
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button data-testid="contract-next-btn" onClick={handleNext} className="gap-2">
                      {t("التالي", "Next")}
                      {direction === "rtl" ? (
                        <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
                      ) : (
                        <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </DialogFooter>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <UnsavedChangesDialog blocker={blocker} />
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onStay={() => setConfirmDiscardOpen(false)}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
